import React, { useEffect, useState } from 'react';
import {
  Box, Typography, Card, CardContent, Grid, Chip,
  CircularProgress, Alert, Avatar, Button, Dialog,
  DialogTitle, DialogContent, DialogActions, TextField,
  Select, MenuItem, FormControl, InputLabel, Switch,
  FormControlLabel, Table, TableBody, TableCell,
  TableContainer, TableHead, TableRow, Paper,
  Divider, InputAdornment
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import { getUsuarios, crearUsuario, actualizarUsuario, actualizarDatosLaborales,
  getConfigNotificaciones, activarNotificacion, desactivarNotificacion,
  getPermisosUsuario, setPermiso, quitarPermiso,resetear2FAUsuario,
  getOrganigrama, asignarResponsable, getFichajesEquipo,
  getDocumentacion, subirDocumentacion, borrarDocumentacion,
  descargarDocumentacion, getCostesUsuario, actualizarCostesUsuario,
  eliminarUsuario, reenviarAcceso } from '../api';
import { ARBOL_PERMISOS } from '../config/permisos';
import * as XLSX from 'xlsx';

interface Usuario {
  id: number;
  nombre: string;
  apellidos: string;
  email: string;
  rol: string;
  puesto?: string;
  activo: number;
  foto: string;
}

const TIPOS_NOTIFICACION = [
  { key: 'nueva_tarea', label: '📌 Nueva tarea asignada' },
  { key: 'nueva_intervencion', label: '🔧 Nueva intervención registrada' },
  { key: 'solicitud_material', label: '📦 Solicitud de material en intervención' },
  { key: 'intervencion_pendiente_aprobar', label: '✅ Informe de intervención pendiente de aprobar' },
  { key: 'solicitud_ausencia', label: '🌴 Solicitud de vacaciones o libres' },
  { key: 'gasto_pendiente_validar', label: '💳 Gasto de intervención pendiente de validar' },
  { key: 'venta_pendiente_facturar', label: '🧾 Venta pendiente de facturar' },
  { key: 'pedido_compra_emitido', label: '🛒 Pedido de compra emitido' },
  { key: 'factura_proveedor_registrada', label: '📥 Factura de proveedor registrada' },
  { key: 'resumen_diario', label: '📊 Resumen diario de facturación y pagos' },
];

export default function Usuarios() {
  const [usuarios, setUsuarios] = useState<Usuario[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [laboralDialog, setLaboralDialog] = useState<any>(null);
  const [datosLaborales, setDatosLaborales] = useState({ fecha_alta_empresa: '', dias_vacaciones_anuales: 22, dias_libre_disposicion: 0 });
  const [configNotif, setConfigNotif] = useState<any[]>([]);
  const [notifDialog, setNotifDialog] = useState<any>(null);
  const [permisosDialog, setPermisosDialog] = useState<any>(null);
  const [costes, setCostes] = useState<Record<string, string>>({});
  const [conceptosCoste, setConceptosCoste] = useState<any[]>([]);
  const [responsableDialog, setResponsableDialog] = useState<any>(null);
  const [responsableSeleccionado, setResponsableSeleccionado] = useState<number | null>(null);
  const [fichajesDialog, setFichajesDialog] = useState<any>(null);
  const [fichajesEquipo, setFichajesEquipo] = useState<any[]>([]);
  const [fichajesFiltros, setFichajesFiltros] = useState({ desde: '', hasta: '' });
  const [docsDialog, setDocsDialog] = useState<any>(null);
  const [docsUsuario, setDocsUsuario] = useState<any[]>([]);
  const [nuevoDoc, setNuevoDoc] = useState({ titulo: '', tipo: 'nomina', file: null as File | null });
  const [misPermisosUsuario, setMisPermisosUsuario] = useState<string[]>([]);
  const [buscarPermiso, setBuscarPermiso] = useState('');
  const [nuevo, setNuevo] = useState({ nombre: '', apellidos: '', email: '', password: '', puesto: '', esAdmin: false });
  const [permisosNuevo, setPermisosNuevo] = useState<string[]>([]);
  const [guardando, setGuardando] = useState(false);
  const usuarioActual = JSON.parse(localStorage.getItem('user') || '{}');
  const [misPermisosActual, setMisPermisosActual] = useState<string[]>([]);
  const puedeGestionarDocs = usuarioActual.rol === 'admin' || misPermisosActual.includes('gestionar_documentacion');
  const puedeVerCostes = usuarioActual.rol === 'admin' || misPermisosActual.includes('costes_personal');  
  const soloJornadas = usuarioActual.rol !== 'admin'
    && !misPermisosActual.includes('ver_usuarios')
    && misPermisosActual.includes('ver_todas_jornadas');

  const cargar = () => {
    setLoading(true);
    getUsuarios()
      .then(setUsuarios)
      .catch(() => setError('Error cargando usuarios'))
      .finally(() => setLoading(false));
    getConfigNotificaciones().then(setConfigNotif);
  };

  useEffect(() => { cargar(); }, []);

  useEffect(() => {
    if (usuarioActual.rol !== 'admin') {
      import('../api').then(({ getMisPermisos }) => {
        getMisPermisos().then(setMisPermisosActual).catch(() => setMisPermisosActual([]));
      });
    }
  }, []);

  const tieneNotificacionActiva = (tecnicoId: number, tipo: string) => {
    return configNotif.some(c => c.tecnico_id === tecnicoId && c.tipo_notificacion === tipo && c.activo);
  };

  const handleToggleNotificacion = async (tecnicoId: number, tipo: string) => {
    const activa = tieneNotificacionActiva(tecnicoId, tipo);
    if (activa) {
      await desactivarNotificacion(tecnicoId, tipo);
    } else {
      await activarNotificacion(tecnicoId, tipo);
    }
    getConfigNotificaciones().then(setConfigNotif);
  };

  const handleReenviarAcceso = async (u: any) => {
    if (!window.confirm(
      `¿Enviar una contraseña nueva a ${u.nombre}?\n\n` +
      `Se generará una contraseña y se le enviará por correo a ${u.email}. ` +
      `La contraseña actual dejará de funcionar.`)) return;
    try {
      const r = await reenviarAcceso(u.id);
      alert(`✅ ${r.mensaje}`);
    } catch (e: any) {
      alert(e.response?.data?.detail || 'Error al enviar el acceso');
    }
  };

  const handleEliminarUsuario = async (u: any) => {
    if (!window.confirm(
      `¿Eliminar a ${u.nombre} ${u.apellidos || ''}?\n\n` +
      `Si tiene histórico en el CRM (intervenciones, fichajes, tareas...), se desactivará ` +
      `en lugar de borrarse, para no perder la trazabilidad.`)) return;
    try {
      const r = await eliminarUsuario(u.id);
      alert(r.accion === 'eliminado' ? `✅ ${r.mensaje}` : `ℹ️ ${r.mensaje}`);
      cargar();
    } catch (e: any) {
      alert(e.response?.data?.detail || 'Error al eliminar el usuario');
    }
  };

  const handleResetear2FA = async (u: any) => {
    if (!window.confirm(
      `¿Resetear la verificación en dos pasos de ${u.nombre} ${u.apellidos || ''}?\n\n` +
      `Tendrá que volver a configurarla con su móvil la próxima vez que entre.`)) return;
    try {
      const res = await resetear2FAUsuario(u.id);
      alert(`✅ ${res.mensaje}`);
    } catch (e: any) {
      alert(e.response?.data?.detail || 'Error al resetear el 2FA');
    }
  };

  const handleAbrirPermisos = async (u: Usuario) => {
    setPermisosDialog(u);
    const permisos = await getPermisosUsuario(u.id);
    setMisPermisosUsuario(permisos);
  };

  const tienePermiso = (clave: string) => misPermisosUsuario.includes(clave);

  const handleTogglePermiso = async (clave: string) => {
    if (!permisosDialog) return;
    if (tienePermiso(clave)) {
      await quitarPermiso(permisosDialog.id, clave);
      setMisPermisosUsuario(prev => prev.filter(p => p !== clave));
    } else {
      await setPermiso(permisosDialog.id, clave);
      setMisPermisosUsuario(prev => [...prev, clave]);
    }
  };

const handleCrear = async () => {
    setGuardando(true);
    try {
      const result = await crearUsuario({
        nombre: nuevo.nombre, apellidos: nuevo.apellidos,
        email: nuevo.email, password: nuevo.password,
        puesto: nuevo.puesto,
        rol: nuevo.esAdmin ? 'admin' : 'usuario',
      });
      if (result.id) {
        for (const permiso of permisosNuevo) {
          await setPermiso(result.id, permiso);
        }
      }
      setDialogOpen(false);
      setNuevo({ nombre: '', apellidos: '', email: '', password: '', puesto: '', esAdmin: false });
      setPermisosNuevo([]);
      cargar();
    } catch {
      setError('Error al crear usuario — email ya existe');
    } finally {
      setGuardando(false);
    }
  };

  const handleToggleActivo = async (u: Usuario) => {
    await actualizarUsuario(u.id, { activo: u.activo ? 0 : 1 });
    cargar();
  };

  const handleToggleAdmin = async (u: Usuario) => {
    await actualizarUsuario(u.id, { rol: u.rol === 'admin' ? 'usuario' : 'admin' });
    cargar();
  };

  const handleGuardarPuesto = async (id: number, puesto: string) => {
    await actualizarUsuario(id, { puesto });
    cargar();
  };

  const fichajesEquipoFiltrados = fichajesEquipo.filter(f => {
    if (fichajesFiltros.desde && f.fecha < fichajesFiltros.desde) return false;
    if (fichajesFiltros.hasta && f.fecha > fichajesFiltros.hasta) return false;
    return true;
  });

  const totalHorasEquipo = fichajesEquipoFiltrados.reduce(
    (sum, f) => sum + (f.horas_totales || 0), 0
  );

  const exportarFichajesUsuarioExcel = () => {
    if (!fichajesDialog) return;
    const datos = fichajesEquipoFiltrados.map(f => ({
      'Fecha': f.fecha,
      'Entrada': f.hora_entrada || '—',
      'Inicio pausa': f.hora_inicio_pausa || '—',
      'Fin pausa': f.hora_fin_pausa || '—',
      'Salida': f.hora_salida || '—',
      'Horas totales': f.horas_totales || 0,
    }));
    const ws = XLSX.utils.json_to_sheet(datos);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Fichajes');
    XLSX.writeFile(wb, `fichajes_${fichajesDialog.nombre}_${new Date().toISOString().slice(0,10)}.xlsx`);
  };

  const handleAbrirDocs = async (u: Usuario) => {
    setDocsDialog(u);
    setNuevoDoc({ titulo: '', tipo: 'nomina', file: null });
    try {
      const docs = await getDocumentacion(u.id);
      setDocsUsuario(docs);
    } catch {
      setDocsUsuario([]);
    }
  };

  const handleSubirDoc = async () => {
    if (!docsDialog || !nuevoDoc.file || !nuevoDoc.titulo) {
      alert('Indica un título y selecciona un archivo');
      return;
    }
    try {
      await subirDocumentacion(docsDialog.id, nuevoDoc.titulo, nuevoDoc.tipo, nuevoDoc.file);
      const docs = await getDocumentacion(docsDialog.id);
      setDocsUsuario(docs);
      setNuevoDoc({ titulo: '', tipo: 'nomina', file: null });
    } catch (e: any) {
      alert(e.response?.data?.detail || 'Error al subir el documento');
    }
  };

  const handleBorrarDoc = async (docId: number) => {
    if (!window.confirm('¿Eliminar este documento?')) return;
    try {
      await borrarDocumentacion(docId);
      if (docsDialog) {
        const docs = await getDocumentacion(docsDialog.id);
        setDocsUsuario(docs);
      }
    } catch (e: any) {
      alert(e.response?.data?.detail || 'Error al eliminar');
    }
  };

  const handleAbrirFichajes = async (u: Usuario) => {
    setFichajesDialog(u);
    try {
      const todos = await getFichajesEquipo();
      setFichajesEquipo(todos.filter((f: any) => f.tecnico_id === u.id));
    } catch {
      setFichajesEquipo([]);
    }
  };

  const handleAbrirResponsable = (u: Usuario) => {
    setResponsableDialog(u);
    setResponsableSeleccionado((u as any).responsable_id || null);
  };

  const handleGuardarResponsable = async () => {
    if (!responsableDialog) return;
    try {
      await asignarResponsable(responsableDialog.id, responsableSeleccionado);
      setResponsableDialog(null);
      cargar();
    } catch (e: any) {
      setError(e.response?.data?.detail || 'Error al asignar responsable');
    }
  };

  const handleAbrirLaboral = async (u: Usuario) => {
    setLaboralDialog(u);
    setDatosLaborales({
      fecha_alta_empresa: (u as any).fecha_alta_empresa || '',
      dias_vacaciones_anuales: (u as any).dias_vacaciones_anuales || 22,
      dias_libre_disposicion: (u as any).dias_libre_disposicion || 0
    });
    setCostes({});
    setConceptosCoste([]);
    if (puedeVerCostes) {
      try {
        const d = await getCostesUsuario(u.id);
        setConceptosCoste(d.conceptos || []);
        const iniciales: Record<string, string> = {};
        (d.conceptos || []).forEach((c: any) => {
          iniciales[c.clave] = String(d.costes?.[c.clave] ?? 0);
        });
        setCostes(iniciales);
      } catch { /* sin permiso */ }
    }
  };

  const handleGuardarLaboral = async () => {
    if (!laboralDialog) return;
    await actualizarDatosLaborales(laboralDialog.id, datosLaborales);
    if (puedeVerCostes && conceptosCoste.length > 0) {
      try {
        const numericos: Record<string, number> = {};
        Object.entries(costes).forEach(([k, v]) => { numericos[k] = parseFloat(v) || 0; });
        await actualizarCostesUsuario(laboralDialog.id, numericos);
      } catch (e: any) {
        alert(e.response?.data?.detail || 'Error al guardar los costes');
      }
    }
    setLaboralDialog(null);
    cargar();
  };

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4" sx={{ fontWeight: 600 }}>👷 Usuarios</Typography>
        {usuarioActual.rol === 'admin' && <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => setDialogOpen(true)}
          sx={{ bgcolor: '#4f46e5', '&:hover': { bgcolor: '#3730a3' }, borderRadius: 2 }}
        >
          Nuevo usuario
        </Button>}
      </Box>

      {loading && <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}><CircularProgress /></Box>}
      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        {usuarios.length} usuarios
      </Typography>

      <Grid container spacing={2}>
        {usuarios.map((u) => (
          <Grid item xs={12} sm={6} lg={4} key={u.id}>
            <Card sx={{ borderRadius: 3, opacity: u.activo ? 1 : 0.6 }}>
              <CardContent>
                <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', mb: 2 }}>
                  <Avatar
                    src={u.foto || ''}
                    sx={{ width: 48, height: 48, bgcolor: '#4f46e5' }}
                  >
                    {u.nombre[0]}
                  </Avatar>
                  <Box sx={{ flex: 1 }}>
                    <Typography sx={{ fontWeight: 600 }}>
                      {u.rol === 'admin' ? '🔑 ' : ''}{u.nombre} {u.apellidos}
                    </Typography>
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                      {u.puesto || 'Sin puesto asignado'}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">{u.email}</Typography>
                  </Box>
                  <Chip
                    label={u.activo ? 'Activo' : 'Inactivo'}
                    color={u.activo ? 'success' : 'default'}
                    size="small"
                  />
                </Box>

                <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', flexWrap: 'wrap' }}>
                  <TextField
                    size="small" label="Puesto" sx={{ minWidth: 200 }}
                    placeholder="Ej: Técnico y encargado de almacén"
                    defaultValue={u.puesto || ''}
                    onBlur={(e) => {
                      const valor = e.target.value.trim();
                      if (valor !== (u.puesto || '')) handleGuardarPuesto(u.id, valor);
                    }}
                  />
                  {usuarioActual.rol === 'admin' && (
                    <FormControlLabel
                      control={
                        <Switch
                          checked={u.rol === 'admin'}
                          onChange={() => handleToggleAdmin(u)}
                          size="small" color="error"
                        />
                      }
                      label="🔑 Admin"
                    />
                  )}
                  <FormControlLabel
                    control={
                      <Switch
                        checked={!!u.activo}
                        onChange={() => handleToggleActivo(u)}
                        size="small"
                      />
                    }
                    label={u.activo ? 'Activo' : 'Inactivo'}
                  />
                </Box>

                <Box sx={{ display: 'flex', gap: 1, mt: 1, flexWrap: 'wrap' }}>
                  {!soloJornadas && (
                    <>
                      <Button
                        size="small" variant="text"
                        onClick={() => handleAbrirLaboral(u)}
                      >
                        📋 Datos laborales
                      </Button>
                      <Button
                        size="small" variant="text"
                        onClick={() => setNotifDialog(u)}
                      >
                        🔔 Notificaciones
                      </Button>
                      <Button
                        size="small" variant="text"
                        onClick={() => handleAbrirPermisos(u)}
                      >
                        🔐 Permisos
                      </Button>

                      <Button
                        size="small" variant="text" color="warning"
                        onClick={() => handleResetear2FA(u)}
                      >
                        🔓 Resetear 2FA
                      </Button>
                      {usuarioActual.rol === 'admin' && (
                        <Button size="small" variant="text"
                          onClick={() => handleReenviarAcceso(u)}>
                          📧 Reenviar acceso
                        </Button>
                      )}
                      {usuarioActual.rol === 'admin' && u.id !== usuarioActual.id && (
                        <Button
                          size="small" variant="text" color="error"
                          onClick={() => handleEliminarUsuario(u)}
                        >
                          🗑 Eliminar
                        </Button>
                      )}

                      <Button
                        size="small" variant="text"
                        onClick={() => handleAbrirResponsable(u)}
                      >
                        👔 Responsable
                      </Button>
                    </>
                  )}
                  <Button
                    size="small" variant="text"
                    onClick={() => handleAbrirFichajes(u)}
                  >
                    🕐 Fichajes
                  </Button>
                  {!soloJornadas && puedeGestionarDocs && (
                    <Button
                      size="small" variant="text"
                      onClick={() => handleAbrirDocs(u)}
                    >
                      🔐 Documentación
                    </Button>
                  )}
                </Box>
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>

      {/* Dialog documentación confidencial */}
      <Dialog open={!!docsDialog} onClose={(e, r) => { if (r !== 'backdropClick' && r !== 'escapeKeyDown') { setDocsDialog(null); } }} maxWidth="md" fullWidth>
        <DialogTitle>🔐 Documentación — {docsDialog?.nombre} {docsDialog?.apellidos}</DialogTitle>
        <DialogContent>
          {/* Subir nuevo documento */}
          <Card sx={{ mb: 3, mt: 1, bgcolor: 'background.default' }}>
            <CardContent>
              <Typography variant="subtitle2" sx={{ mb: 2 }}>Subir nuevo documento</Typography>
              <Grid container spacing={2} alignItems="center">
                <Grid item xs={12} sm={3}>
                  <FormControl fullWidth size="small">
                    <InputLabel>Tipo</InputLabel>
                    <Select value={nuevoDoc.tipo} label="Tipo"
                      onChange={(e) => setNuevoDoc({ ...nuevoDoc, tipo: e.target.value })}>
                      <MenuItem value="nomina">Nómina</MenuItem>
                      <MenuItem value="contrato">Contrato</MenuItem>
                      <MenuItem value="otro">Otro</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12} sm={4}>
                  <TextField fullWidth size="small" label="Título"
                    value={nuevoDoc.titulo}
                    onChange={(e) => setNuevoDoc({ ...nuevoDoc, titulo: e.target.value })} />
                </Grid>
                <Grid item xs={12} sm={3}>
                  <Button variant="outlined" component="label" size="small" fullWidth>
                    {nuevoDoc.file ? '✓ Archivo' : 'Elegir archivo'}
                    <input type="file" hidden
                      onChange={(e) => setNuevoDoc({ ...nuevoDoc, file: e.target.files?.[0] || null })} />
                  </Button>
                </Grid>
                <Grid item xs={12} sm={2}>
                  <Button fullWidth variant="contained" size="small" sx={{ bgcolor: '#4f46e5' }}
                    onClick={handleSubirDoc} disabled={!nuevoDoc.file || !nuevoDoc.titulo}>
                    Subir
                  </Button>
                </Grid>
              </Grid>
              {nuevoDoc.file && (
                <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                  {nuevoDoc.file.name}
                </Typography>
              )}
            </CardContent>
          </Card>

          {/* Lista de documentos */}
          {docsUsuario.length === 0 ? (
            <Alert severity="info">Este empleado no tiene documentos todavía.</Alert>
          ) : (
            <TableContainer component={Paper} sx={{ borderRadius: 2 }}>
              <Table size="small">
                <TableHead>
                  <TableRow sx={{ bgcolor: '#1a1a2e' }}>
                    <TableCell sx={{ color: 'white' }}>Tipo</TableCell>
                    <TableCell sx={{ color: 'white' }}>Título</TableCell>
                    <TableCell sx={{ color: 'white' }}>Fecha</TableCell>
                    <TableCell sx={{ color: 'white' }}>Subido por</TableCell>
                    <TableCell sx={{ color: 'white' }} align="right">Acciones</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {docsUsuario.map((d) => (
                    <TableRow key={d.id}>
                      <TableCell sx={{ textTransform: 'capitalize' }}>{d.tipo}</TableCell>
                      <TableCell>{d.titulo}</TableCell>
                      <TableCell>{d.fecha?.slice(0, 10)}</TableCell>
                      <TableCell>{d.subido_por_nombre || '—'}</TableCell>
                      <TableCell align="right">
                        <Button size="small" variant="outlined" sx={{ mr: 1 }}
                          onClick={() => descargarDocumentacion(d.id, d.titulo)}>
                          ⬇
                        </Button>
                        <Button size="small" variant="outlined" color="error"
                          onClick={() => handleBorrarDoc(d.id)}>
                          🗑
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDocsDialog(null)} variant="contained" sx={{ bgcolor: '#4f46e5' }}>
            Cerrar
          </Button>
        </DialogActions>
      </Dialog>

      {/* Dialog fichajes del equipo */}
      <Dialog open={!!fichajesDialog} onClose={(e, r) => { if (r !== 'backdropClick' && r !== 'escapeKeyDown') { setFichajesDialog(null); } }} maxWidth="md" fullWidth>
        <DialogTitle>🕐 Fichajes — {fichajesDialog?.nombre} {fichajesDialog?.apellidos}</DialogTitle>
        <DialogContent>
          {/* Filtros */}
          <Grid container spacing={2} sx={{ mb: 2, mt: 0.5 }} alignItems="center">
            <Grid item xs={12} sm={3}>
              <TextField fullWidth size="small" label="Desde" type="date"
                InputLabelProps={{ shrink: true }}
                value={fichajesFiltros.desde}
                onChange={(e) => setFichajesFiltros({ ...fichajesFiltros, desde: e.target.value })} />
            </Grid>
            <Grid item xs={12} sm={3}>
              <TextField fullWidth size="small" label="Hasta" type="date"
                InputLabelProps={{ shrink: true }}
                value={fichajesFiltros.hasta}
                onChange={(e) => setFichajesFiltros({ ...fichajesFiltros, hasta: e.target.value })} />
            </Grid>
            <Grid item xs={12} sm={6} sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
              <Button size="small" variant="text" onClick={() => setFichajesFiltros({ desde: '', hasta: '' })}>Limpiar</Button>
              <Button size="small" variant="contained" sx={{ bgcolor: '#4f46e5' }}
                onClick={exportarFichajesUsuarioExcel} disabled={fichajesEquipoFiltrados.length === 0}>
                📊 Excel
              </Button>
            </Grid>
          </Grid>

          <Chip label={`${fichajesEquipoFiltrados.length} fichajes · Total: ${totalHorasEquipo.toFixed(2)}h`}
            color="info" sx={{ mb: 2 }} />

          {fichajesEquipoFiltrados.length === 0 ? (
            <Alert severity="info" sx={{ mt: 1 }}>No hay fichajes en este periodo.</Alert>
          ) : (
            <TableContainer component={Paper} sx={{ borderRadius: 2, mt: 1 }}>
              <Table size="small">
                <TableHead>
                  <TableRow sx={{ bgcolor: '#1a1a2e' }}>
                    {['Fecha', 'Entrada', 'Pausa', 'Salida', 'Horas'].map(h => (
                      <TableCell key={h} sx={{ color: 'white' }}>{h}</TableCell>
                    ))}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {fichajesEquipoFiltrados.map((f, i) => (
                    <TableRow key={i}>
                      <TableCell>{f.fecha}</TableCell>
                      <TableCell>{f.hora_entrada || '—'}</TableCell>
                      <TableCell>{f.hora_inicio_pausa ? `${f.hora_inicio_pausa} - ${f.hora_fin_pausa || '...'}` : '—'}</TableCell>
                      <TableCell>{f.hora_salida || '—'}</TableCell>
                      <TableCell>{f.horas_totales ? `${f.horas_totales}h` : '—'}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setFichajesDialog(null)} variant="contained" sx={{ bgcolor: '#4f46e5' }}>
            Cerrar
          </Button>
        </DialogActions>
      </Dialog>

      {/* Dialog responsable */}
      <Dialog open={!!responsableDialog} onClose={(e, r) => { if (r !== 'backdropClick' && r !== 'escapeKeyDown') { setResponsableDialog(null); } }} maxWidth="sm" fullWidth>
        <DialogTitle>👔 Responsable — {responsableDialog?.nombre} {responsableDialog?.apellidos}</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2, mt: 1 }}>
            Asigna el responsable directo (superior) de este usuario. Déjalo vacío solo si es el director general.
          </Typography>
          <FormControl fullWidth>
            <InputLabel>Responsable directo</InputLabel>
            <Select
              value={responsableSeleccionado ?? ''}
              label="Responsable directo"
              onChange={(e) => setResponsableSeleccionado(e.target.value === '' ? null : Number(e.target.value))}
            >
              <MenuItem value="">— Sin responsable (Director general) —</MenuItem>
              {usuarios
                .filter(u => u.id !== responsableDialog?.id)
                .map(u => (
                  <MenuItem key={u.id} value={u.id}>
                    {u.nombre} {u.apellidos}{u.puesto ? ` — ${u.puesto}` : ''}
                  </MenuItem>
                ))}
            </Select>
          </FormControl>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setResponsableDialog(null)}>Cancelar</Button>
          <Button onClick={handleGuardarResponsable} variant="contained" sx={{ bgcolor: '#4f46e5' }}>
            Guardar
          </Button>
        </DialogActions>
      </Dialog>

      {/* Dialog datos laborales */}
      <Dialog open={!!laboralDialog} onClose={(e, r) => { if (r !== 'backdropClick' && r !== 'escapeKeyDown') { setLaboralDialog(null); } }} maxWidth="sm" fullWidth>
        <DialogTitle>📋 Datos laborales — {laboralDialog?.nombre} {laboralDialog?.apellidos}</DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
            <TextField
              label="Fecha de alta en la empresa" type="date"
              InputLabelProps={{ shrink: true }}
              value={datosLaborales.fecha_alta_empresa}
              onChange={(e) => setDatosLaborales({ ...datosLaborales, fecha_alta_empresa: e.target.value })}
            />
            <TextField
              label="Días de vacaciones anuales" type="number"
              value={datosLaborales.dias_vacaciones_anuales}
              onChange={(e) => setDatosLaborales({ ...datosLaborales, dias_vacaciones_anuales: parseInt(e.target.value) || 0 })}
            />
            <TextField
              label="Días de libre disposición anuales" type="number"
              value={datosLaborales.dias_libre_disposicion}
              onChange={(e) => setDatosLaborales({ ...datosLaborales, dias_libre_disposicion: parseInt(e.target.value) || 0 })}
            />
            {puedeVerCostes && conceptosCoste.length > 0 && (
              <>
                <Divider sx={{ my: 1 }} />
                <Typography variant="subtitle2" sx={{ color: '#4f46e5' }}>
                  💰 Costes internos (confidencial)
                </Typography>
                <Typography variant="caption" color="text.secondary" sx={{ mb: 1 }}>
                  Coste real para la empresa de cada tipo de hora. Se usa para calcular
                  el margen de las intervenciones.
                </Typography>
                <Table size="small">
                  <TableBody>
                    {conceptosCoste.map((c: any) => (
                      <TableRow key={c.clave}>
                        <TableCell sx={{ border: 0, py: 0.5, pl: 0 }}>
                          <Typography variant="body2">{c.nombre}</Typography>
                        </TableCell>
                        <TableCell sx={{ border: 0, py: 0.5, pr: 0, width: 130 }} align="right">
                          <TextField
                            size="small" type="number" variant="standard"
                            value={costes[c.clave] ?? '0'}
                            onChange={(e) => setCostes({ ...costes, [c.clave]: e.target.value })}
                            inputProps={{ style: { textAlign: 'right' } }}
                            InputProps={{ endAdornment: <InputAdornment position="end">€</InputAdornment> }}
                            sx={{ width: 120 }}
                          />
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </>
            )}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setLaboralDialog(null)}>Cancelar</Button>
          <Button onClick={handleGuardarLaboral} variant="contained" sx={{ bgcolor: '#4f46e5' }}>
            Guardar
          </Button>
        </DialogActions>
      </Dialog>

      {/* Dialog notificaciones */}
      <Dialog open={!!notifDialog} onClose={(e, r) => { if (r !== 'backdropClick' && r !== 'escapeKeyDown') { setNotifDialog(null); } }} maxWidth="sm" fullWidth>
        <DialogTitle>🔔 Notificaciones — {notifDialog?.nombre} {notifDialog?.apellidos}</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Selecciona qué notificaciones por email quieres que reciba este usuario.
          </Typography>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
            {TIPOS_NOTIFICACION.map(t => (
              <FormControlLabel
                key={t.key}
                control={
                  <Switch
                    checked={notifDialog ? tieneNotificacionActiva(notifDialog.id, t.key) : false}
                    onChange={() => notifDialog && handleToggleNotificacion(notifDialog.id, t.key)}
                  />
                }
                label={t.label}
              />
            ))}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setNotifDialog(null)} variant="contained" sx={{ bgcolor: '#4f46e5' }}>
            Cerrar
          </Button>
        </DialogActions>
      </Dialog>

      {/* Dialog permisos */}
      <Dialog open={!!permisosDialog} onClose={(e, r) => { if (r !== 'backdropClick' && r !== 'escapeKeyDown') { setPermisosDialog(null); } }} maxWidth="md" fullWidth>
        <DialogTitle>🔐 Permisos — {permisosDialog?.nombre} {permisosDialog?.apellidos}</DialogTitle>
        <DialogContent>
          <TextField fullWidth size="small" placeholder="Buscar permiso..." sx={{ mt: 1, mb: 2 }}
            value={buscarPermiso} onChange={(e) => setBuscarPermiso(e.target.value)} />

          {ARBOL_PERMISOS.map(grupo => {
            const visibles = grupo.permisos.filter(p =>
              !buscarPermiso.trim() ||
              p.label.toLowerCase().includes(buscarPermiso.toLowerCase()) ||
              grupo.modulo.toLowerCase().includes(buscarPermiso.toLowerCase()));
            if (!visibles.length) return null;
            const activos = grupo.permisos.filter(p => tienePermiso(p.key)).length;
            const todos = activos === grupo.permisos.length;

            return (
              <Box key={grupo.modulo} sx={{ mb: 2.5 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, pb: 0.5,
                           borderBottom: '2px solid', borderColor: activos ? '#4f46e5' : 'divider' }}>
                  <Typography sx={{ fontWeight: 700, flex: 1 }}>{grupo.modulo}</Typography>
                  <Chip size="small" label={`${activos} de ${grupo.permisos.length}`}
                    color={todos ? 'success' : activos ? 'primary' : 'default'}
                    variant={activos ? 'filled' : 'outlined'} />
                  <Button size="small" onClick={() => grupo.permisos.forEach(p => {
                    if (tienePermiso(p.key) === todos) handleTogglePermiso(p.key);
                  })}>
                    {todos ? 'Quitar todo' : 'Dar todo'}
                  </Button>
                </Box>

                <Box sx={{ pl: 2, pt: 0.5 }}>
                  {visibles.map((p, idx) => (
                    <Box key={p.key} sx={{ display: 'flex', alignItems: 'center',
                      position: 'relative', pl: 2.5,
                      '&::before': {
                        content: '""', position: 'absolute', left: 0, top: 0,
                        bottom: idx === visibles.length - 1 ? '50%' : 0,
                        borderLeft: '1px solid', borderColor: 'divider',
                      },
                      '&::after': {
                        content: '""', position: 'absolute', left: 0, top: '50%', width: 14,
                        borderTop: '1px solid', borderColor: 'divider',
                      },
                    }}>
                      <FormControlLabel sx={{ m: 0 }}
                        control={<Switch size="small" checked={tienePermiso(p.key)}
                          onChange={() => handleTogglePermiso(p.key)} />}
                        label={<Typography variant="body2">{p.label}</Typography>} />
                    </Box>
                  ))}
                </Box>
              </Box>
            );
          })}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPermisosDialog(null)} variant="contained" sx={{ bgcolor: '#4f46e5' }}>
            Cerrar
          </Button>
        </DialogActions>
      </Dialog>

      {/* Dialog nuevo usuario */}
      <Dialog open={dialogOpen} onClose={(e, r) => { if (r !== 'backdropClick' && r !== 'escapeKeyDown') { setDialogOpen(false); } }} maxWidth="sm" fullWidth>
        <DialogTitle>➕ Nuevo usuario</DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
            <Box sx={{ display: 'flex', gap: 2 }}>
              <TextField
                fullWidth label="Nombre" value={nuevo.nombre}
                onChange={(e) => setNuevo({ ...nuevo, nombre: e.target.value })}
              />
              <TextField
                fullWidth label="Apellidos" value={nuevo.apellidos}
                onChange={(e) => setNuevo({ ...nuevo, apellidos: e.target.value })}
              />
            </Box>
            <TextField
              fullWidth label="Email" type="email" value={nuevo.email}
              onChange={(e) => setNuevo({ ...nuevo, email: e.target.value })}
            />
            <TextField
              fullWidth label="Contraseña" type="password" value={nuevo.password}
              onChange={(e) => setNuevo({ ...nuevo, password: e.target.value })}
            />
            <TextField
              fullWidth label="Puesto"
              placeholder="Ej: Técnico y encargado de almacén"
              value={nuevo.puesto}
              onChange={(e) => setNuevo({ ...nuevo, puesto: e.target.value })}
              helperText="Descripción libre del puesto de trabajo. No afecta a los permisos."
            />
            {usuarioActual.rol === 'admin' && (
              <FormControlLabel
                control={
                  <Switch
                    checked={nuevo.esAdmin} color="error"
                    onChange={(e) => setNuevo({ ...nuevo, esAdmin: e.target.checked })}
                  />
                }
                label="🔑 Administrador (acceso total al sistema)"
              />
            )}

            {!nuevo.esAdmin && (
              <>
                {ARBOL_PERMISOS.map(grupo => {
                  const activos = grupo.permisos.filter(p => permisosNuevo.includes(p.key)).length;
                  const todos = activos === grupo.permisos.length;
                  return (
                    <Box key={grupo.modulo} sx={{ mb: 2 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, pb: 0.5,
                                 borderBottom: '2px solid', borderColor: activos ? '#4f46e5' : 'divider' }}>
                        <Typography sx={{ fontWeight: 700, flex: 1 }}>{grupo.modulo}</Typography>
                        <Chip size="small" label={`${activos} de ${grupo.permisos.length}`}
                          color={todos ? 'success' : activos ? 'primary' : 'default'}
                          variant={activos ? 'filled' : 'outlined'} />
                        <Button size="small" onClick={() => setPermisosNuevo(prev =>
                          todos ? prev.filter(k => !grupo.permisos.some(p => p.key === k))
                                : Array.from(new Set([...prev, ...grupo.permisos.map(p => p.key)]))
                        )}>
                          {todos ? 'Quitar todo' : 'Dar todo'}
                        </Button>
                      </Box>
                      <Box sx={{ pl: 2, pt: 0.5 }}>
                        {grupo.permisos.map((p, idx) => (
                          <Box key={p.key} sx={{ display: 'flex', alignItems: 'center',
                            position: 'relative', pl: 2.5,
                            '&::before': {
                              content: '""', position: 'absolute', left: 0, top: 0,
                              bottom: idx === grupo.permisos.length - 1 ? '50%' : 0,
                              borderLeft: '1px solid', borderColor: 'divider',
                            },
                            '&::after': {
                              content: '""', position: 'absolute', left: 0, top: '50%', width: 14,
                              borderTop: '1px solid', borderColor: 'divider',
                            },
                          }}>
                            <FormControlLabel sx={{ m: 0 }}
                              control={<Switch size="small" checked={permisosNuevo.includes(p.key)}
                                onChange={() => setPermisosNuevo(prev =>
                                  prev.includes(p.key) ? prev.filter(k => k !== p.key) : [...prev, p.key]
                                )} />}
                              label={<Typography variant="body2">{p.label}</Typography>} />
                          </Box>
                        ))}
                      </Box>
                    </Box>
                  );
                })}
              </>
            )}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogOpen(false)}>Cancelar</Button>
          <Button
            onClick={handleCrear}
            variant="contained"
            disabled={guardando || !nuevo.nombre || !nuevo.email || !nuevo.password}
            sx={{ bgcolor: '#4f46e5' }}
          >
            {guardando ? <CircularProgress size={20} /> : 'Crear usuario'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}